import React from 'react';
import { useSelector } from 'react-redux';
import SellFundReviewRow from 'src/components/Sell/SellReview/SellFundReviewRow/SellFundReviewRow';
import SellSelectedFundAmount from 'src/components/Sell/SellReview/SellSelectedFundAmount/SellSelectedFundAmount';
import SellAddressReview from 'src/components/Sell/SellReview/SellAddressReview';
import SellBankReview from 'src/components/Sell/SellReview/SellBankReview';
import { getSellTransactionLookup } from 'src/redux/modules/sellTransaction/selectors';
import { SellFund } from 'src/redux/modules/sellTransaction/types';
import { getDateFrequencyString } from 'src/utils/dates/date-utility';
import './AutoSellReview.styles.scss';
import {
  DELIVERY_MAIL_CHECK,
  PERCENT_START_DATE_TOOLTIP,
  UNIT,
} from '../../../settings/settings';
import { getSelectedBank } from '../../../redux/modules/bankSelection/bankSelection.selectors';
import {
  getFundsByAddressLookup,
  getSelectedSellFundsList,
} from '../../../redux/modules/sellTransaction/sellTransaction.selectors';
import { mailCheckToNamePicker } from 'src/utils/funds/funds-utility';
import ToolTip from '../../Shared/ToolTip/ToolTip';
import { currency } from '../../../utils/utils';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from 'src/redux/modules/authentication/authentication.module.selectors';

export interface AutoSellReviewPanelProps {
  funds: SellFund[];
  deliveryMethod?;
  startDates?;
}

export const AutoSellReviewPanel: React.FC<AutoSellReviewPanelProps> = ({
  funds,
  deliveryMethod,
  startDates = {},
}) => {
  const customerWebViewEnabled: boolean = useSelector(isCustomerWebViewEnabled);
  const isSalesforceRepEnabled: boolean = useSelector(isSalesforceRep);
  const sellLookup = useSelector(getSellTransactionLookup);
  const selectedBank = useSelector(getSelectedBank);
  const selectedFunds = useSelector(getSelectedSellFundsList);
  const hasMultipleNames = false;
  const deductionPerTransaction = 'Deductions Per Transaction';
  const toolTipMessage =
    'The sum of applicable income tax withholding. Go to Step 3 to review.';
  /* eslint-disable */
  const fundsByAddressLookup: any = useSelector(getFundsByAddressLookup);
  /* eslint-enable */
  const showStartDate = React.useCallback(
    // only show the start date column if at least one fund has a start date
    () => funds.some(({ agreementID }) => agreementID in startDates),
    [funds, startDates]
  );

  const isAutoSellAllDollars = funds.every(
    (fund) => fund.grossUnit === UNIT.DOLLARS
  );

  const startDateToolTip = (
    className = 'delivery-method-no-desktop',
    START_DATE_TOOLTIP = 'Automatic Sell programs are typically established the following business day, but can take up to three business days to be completed. Choosing an Automatic Sell date within 3 business days of today’s date may cause your first transaction to occur at the next instance of the chosen date.'
  ) => {
    return (
      <div className={className}>
        EST. START DATE
        <ToolTip message={START_DATE_TOOLTIP} />
      </div>
    );
  };

  return (
    <div>
      <div className="hidden-xs hidden-sm auto-sell-review-heading">
        <div className="auto-sell-review-delivery">DELIVERY METHOD:</div>
        <div className="auto-sell-fund-review-titles">
          <div className="auto-sell-review-titles">
            <div className="auto-sell-from-title">FROM:</div>
            <div className="auto-sell-frequency-title">FREQUENCY:</div>
            {showStartDate() && startDateToolTip('auto-sell-start-date-title')}
            <div className="auto-sell-amount-title">
              {!isAutoSellAllDollars && 'EST.'} AMOUNT:
              {!isAutoSellAllDollars && (
                <ToolTip message={PERCENT_START_DATE_TOOLTIP} />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="sell-transactions-review auto-sell-transactions-review">
        {deliveryMethod !== DELIVERY_MAIL_CHECK && (
          <>
            <div className="auto-sell-transactions-review-mobile-border">
              <p className="delivery-method-no-desktop">DELIVERY METHOD:</p>
              <hr className="hr-tablet-only" />
              <SellBankReview
                deliveryMethod={deliveryMethod}
                selectedBank={selectedBank}
              />
              {!(!customerWebViewEnabled && isSalesforceRepEnabled) && (
                <div className="est-deliver-time">
                  Estimated delivery 2-3 days from sell date.
                </div>
              )}
            </div>
            <div className="auto-sell-transactions-review-space">
              {funds.map((fund, i) => {
                const { selectedDays, selectedMonths } =
                  sellLookup?.[fund.agreementID];
                const federalDeduction = fund.federalWithholdingApplied
                  ? fund.federalWithholdingDollars ?? 0
                  : 0;
                const stateDeduction = fund.stateWithholdingApplied
                  ? fund.stateWithholdingDollars ?? 0
                  : 0;
                const totalDeduction = federalDeduction + stateDeduction;
                return (
                  <>
                    <div
                      key={i}
                      className="auto-sell-transactions-review-funds-row auto-sell-review-row"
                    >
                      <p className="delivery-method-no-desktop">FROM:</p>

                      <div className="auto-sell-fund-review">
                        <SellFundReviewRow fund={fund} />
                        <div className="hidden-xs hidden-sm auto-sell-review-tool-tip-container">
                          <ToolTip
                            callerText={`${deductionPerTransaction}:`}
                            header={deductionPerTransaction}
                            message={toolTipMessage}
                          />
                        </div>
                      </div>
                      <div className="frequency-field">
                        <p className="delivery-method-no-desktop">FREQUENCY:</p>
                        {getDateFrequencyString({
                          selectedDays,
                          selectedMonths,
                        })}
                      </div>

                      <div className="auto-sell-start-date-field">
                        {startDates[fund.agreementID]?.startDate
                          ? startDateToolTip()
                          : null}
                        {startDates[fund.agreementID]?.startDate}
                      </div>

                      <div className="auto-sell-amount-review">
                        <SellSelectedFundAmount fund={fund} />
                        <div className="deduction-amount-value">
                          <div className="hidden-md hidden-lg">
                            <ToolTip
                              callerText={`${deductionPerTransaction}:`}
                              header={deductionPerTransaction}
                              message={toolTipMessage}
                            />
                          </div>
                          -{currency(totalDeduction)}
                        </div>
                      </div>
                    </div>
                    <div className="take-home-amount-container">
                      <div className="total-take-home-review">
                        Total Take-Home Amount
                      </div>
                      <div className="take-home-amount-value">
                        {currency(fund.grossAmountDollars - totalDeduction)}
                      </div>
                    </div>
                  </>
                );
              })}
            </div>
          </>
        )}
        {deliveryMethod === DELIVERY_MAIL_CHECK && (
          <>
            {Object.keys(fundsByAddressLookup).map((Address) => {
              const funds = fundsByAddressLookup[Address].funds;
              const firstFund = funds[0];
              const address = firstFund?.address;
              return (
                <>
                  <div className="auto-sell-transactions-review-mobile-border">
                    <p className="delivery-method-no-desktop">
                      DELIVERY METHOD:
                    </p>
                    <hr className="hr-tablet-only" />
                    <SellAddressReview
                      address={address}
                      name={
                        mailCheckToNamePicker(selectedFunds[0]) ||
                        hasMultipleNames
                      }
                    />
                    {!(!customerWebViewEnabled && isSalesforceRepEnabled) && (
                      <div className="est-deliver-time">
                        Estimated delivery 7-10 days from sell date.
                      </div>
                    )}
                  </div>
                  <div className="auto-sell-transactions-review-space">
                    {funds.map((fund, i) => {
                      const { selectedDays, selectedMonths } =
                        sellLookup?.[fund.agreementID];
                      const federalDeduction = fund.federalWithholdingApplied
                        ? fund.federalWithholdingDollars ?? 0
                        : 0;
                      const stateDeduction = fund.stateWithholdingApplied
                        ? fund.stateWithholdingDollars ?? 0
                        : 0;
                      const totalDeduction = federalDeduction + stateDeduction;
                      return (
                        <>
                          <div
                            key={i}
                            className="auto-sell-transactions-review-funds-row auto-sell-review-row"
                          >
                            <p className="delivery-method-no-desktop">FROM:</p>
                            <div className="auto-sell-fund-review">
                              <SellFundReviewRow fund={fund} />
                              <div className="hidden-xs hidden-sm auto-sell-review-tool-tip-container">
                                <ToolTip
                                  callerText={`${deductionPerTransaction}:`}
                                  header={deductionPerTransaction}
                                  message={toolTipMessage}
                                />
                              </div>
                            </div>
                            <div className="frequency-field">
                              <p className="delivery-method-no-desktop">
                                FREQUENCY:
                              </p>
                              {getDateFrequencyString({
                                selectedDays,
                                selectedMonths,
                              })}
                            </div>
                            <div className="auto-sell-start-date-field">
                              {startDates[fund.agreementID]?.startDate
                                ? startDateToolTip()
                                : null}
                              {startDates[fund.agreementID]?.startDate}
                            </div>
                            <div className="auto-sell-amount-review">
                              <SellSelectedFundAmount fund={fund} />
                              <div className="deduction-amount-value">
                                <div className="hidden-md hidden-lg">
                                  <ToolTip
                                    callerText={`${deductionPerTransaction}:`}
                                    header={deductionPerTransaction}
                                    message={toolTipMessage}
                                  />
                                </div>
                                -{currency(totalDeduction)}
                              </div>
                            </div>
                          </div>
                          <div className="take-home-amount-container">
                            <div className="total-take-home-review">
                              Total Take-Home Amount
                            </div>
                            <div className="take-home-amount-value">
                              {currency(
                                fund.grossAmountDollars - totalDeduction
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })}
                  </div>
                </>
              );
            })}
          </>
        )}
        {/*
            <SellDeductionsReviewRow
              selectedFunds={selectedFunds}
              sellTotalAmount={sellTotalAmount}
              sellTotalDeduction={sellTotalDeduction}
            />
            < / div >

            <SellTradeDateReviewRow
              deliveryMethod={deliveryMethod}
              displayTradeDate={displayTradeDate}
              isTakeHomeEstimated={isTakeHomeEstimated}
              sellTakeHomeAmount={sellTakeHomeAmount}
              sellTotalAmount={sellTotalAmount}
            />
          */}
      </div>
    </div>
  );
};
